import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { Trade } from '@/api';
import { useToast } from '@/components/ui/use-toast';
import { openAuthModal } from '@/store/slices/auth-modal.slice';
import {
	buildAuthRedirectUrl,
	markSignUpAfterTradeAttempt,
	formatDateTimeFull,
	asApiError,
} from '@/utils';
import { useIsMobile } from '@/hooks';
import mixpanel from '../../configs/mixpanel';

export function useMarketTrade({
	market,
	lobbyId,
	user,
	lobby,
	event,
	isRegisteredUser,
	userMatchIds,
	selectedPosition,
	setSelectedPosition,
	setShowTradeInfo,
	onTradeComplete,
	fetchAllRef,
	setShowMyPicksPulseAnimation,
	setNewMarketsTradedCount,
	handleRefetchLeaderboard,
	source = 'market_card',
}: {
	market: any;
	lobbyId: any;
	user: any;
	lobby: any;
	event: any;
	isRegisteredUser: any;
	userMatchIds: any;
	selectedPosition: any;
	setSelectedPosition: (v: any) => void;
	setShowTradeInfo: (v: boolean) => void;
	onTradeComplete?: (trade: any) => void;
	fetchAllRef?: any;
	setShowMyPicksPulseAnimation?: (v: any) => void;
	setNewMarketsTradedCount?: (updater: (prev: number) => number) => void;
	handleRefetchLeaderboard?: () => void;
	source?: string;
}) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isAnimated, setIsAnimated] = useState(false);
	const [blockTrade, setBlockTrade] = useState(false);
	const { toast } = useToast();
	const dispatch = useDispatch();
	const isMobile = useIsMobile();
	const navigate = useNavigate();

	const handleSubmitTrade = async () => {
		if (isSubmitting) return;

		if (!isRegisteredUser) {
			markSignUpAfterTradeAttempt({
				marketId: market.id,
				lobbyId,
				eventId: market.event_id,
				position: selectedPosition?.toLowerCase(),
				market: market.question,
				lobbyName: lobby?.name,
				eventName: event?.title,
				source,
			});
			if (isMobile) {
				dispatch(openAuthModal());
			} else {
				navigate(buildAuthRedirectUrl());
			}
			return;
		}

		try {
			setIsAnimated(true);
			await new Promise((resolve) => setTimeout(resolve, 1200));

			setIsSubmitting(true);
			setBlockTrade(true);

			const isFirstMatchTrade = !userMatchIds?.has(market?.match_id ?? 'overall');

			if (!isRegisteredUser) {
				setIsAnimated(false);
				setIsSubmitting(false);
				setBlockTrade(false);
				return;
			}

			setShowMyPicksPulseAnimation?.(true);
			setNewMarketsTradedCount?.((prev) => prev + 1);

			const { trade: newTrade } = await Trade.makeTrade({
				marketId: market.id,
				lobbyId,
				position: selectedPosition.toLowerCase(),
				isFirstMatchTrade,
			});

			if (isFirstMatchTrade) {
				handleRefetchLeaderboard?.();
			}

			toast({
				title: 'Success',
				description: 'Trade placed successfully',
				className: 'bg-black border-0',
			} as any);

			onTradeComplete?.(newTrade);

			if (fetchAllRef?.current) {
				fetchAllRef.current();
			}

			mixpanel.track('[TRADE PLACED]', {
				lobbyId,
				eventId: market.event_id,
				userEmail: user?.email,
				position: selectedPosition.toLowerCase(),
				market: market.question,
				lobbyName: lobby?.name,
				eventName: event?.title,
				timestamp: formatDateTimeFull(Date.now()),
			});
		} catch (error: unknown) {
			const e = asApiError(error);
			let errorMessage = 'There was an error processing your trade. Please try again.';

			if (e.message && e.message.includes('Network Error')) {
				errorMessage = 'Network connection issue. Please check your connection and try again.';
			}

			if (e.message && e.message.includes('market is closed') && fetchAllRef.current) {
				if (fetchAllRef.current) {
					fetchAllRef.current();
				}
			}

			toast({
				variant: 'destructive',
				title: 'Trade Failed',
				description: errorMessage,
				className: 'bg-black border-0',
			} as any);
			console.error(error);
			throw new Error('Error submitting trade');
		} finally {
			setSelectedPosition(null);
			setShowTradeInfo(false);
			setBlockTrade(false);
			setIsSubmitting(false);
		}
	};

	return { isSubmitting, isAnimated, blockTrade, handleSubmitTrade };
}
